import { Component, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngxs/store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { AlertService, ButtonColors, LayerService } from '@ed---interne/ng-uui-components';
import { emailValidator } from 'src/app/shared/validators/email.validator';
import { EnedisAccessRightModel, EnedisAccessRightProofType } from '../../../enedis.models';
import { CreateAccessRight } from '../../../enedis.state';
import { AuthState } from 'src/app/core/states/auth.state';
import { Observable } from 'rxjs';
import { User } from 'src/app/core/models/user.model';

@UntilDestroy()
@Component({
    selector: 'enedis-access-right-create',
    templateUrl: './enedis-access-right-create.component.html',
    styleUrls: ['./enedis-access-right-create.component.scss'],
})
export class EnedisAccessRightCreateComponent {
    public buttonColors = ButtonColors;

    public header: any;

    public accessRightFormGroup: FormGroup;

    public loggedUser$: Observable<User | null> = inject(Store).select(AuthState.loggedUser);
    public loggedUser: User | undefined;

    public consentMethodOptions = [
        {
            value: 'MAIL',
            label: 'Par email auprès du représentant légal',
        },
        { value: 'FILE', label: `Upload d'un fichier ` },
    ];

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly layerService: LayerService,
        private readonly store: Store,
        private readonly alertService: AlertService,
    ) {
        const now = new Date();
        const twoYearsAgo = new Date(now.getFullYear() - 2, now.getMonth(), now.getDate());
        const fiveYearFromNow = new Date(now.getFullYear() + 5, now.getMonth(), now.getDate());
        this.accessRightFormGroup = this.formBuilder.group({
            clientName: [{ value: '' }],
            clientSirenSiret: [{ value: '' }],
            clientRepresentativeName: [{ value: '' }],
            clientRepresentativeEmail: [{ value: '', disabled: false }, [emailValidator()]],
            pdlNumber: [{ value: '' }],
            validUntil: [{ value: fiveYearFromNow }],
            consumptionAccessStart: [{ value: twoYearsAgo }],
            clientRepresentativePhone: [{ value: '' }],
            comments: [{ value: '' }],
            fileProof: [{ value: undefined }],
            consentMethod: [{ value: undefined }, Validators.required],
        });

        this.accessRightFormGroup.get('consentMethod')?.valueChanges.subscribe((method) => {
            const fileControl = this.accessRightFormGroup.get('fileProof');
            if (method === 'FILE') {
                fileControl?.setValidators([Validators.required]);
            } else {
                fileControl?.clearValidators();
                fileControl?.setValue(undefined);
            }
            fileControl?.updateValueAndValidity();
        });

        this.loggedUser$.pipe(untilDestroyed(this)).subscribe((data: any) => {
            this.loggedUser = data;
        });
    }

    public updateConsumptionAccessStart(date: Date) {
        this.accessRightFormGroup.patchValue({
            consumptionAccessStart: date,
        });
    }

    public updateValidUntil(date: Date) {
        this.accessRightFormGroup.patchValue({
            validUntil: date,
        });
    }
    async onFileUpload(file: File) {
        this.accessRightFormGroup.patchValue({ fileProof: file });
    }

    public valid(): void {
        const accessRightToCreate: Partial<EnedisAccessRightModel> = {
            clientName: this.accessRightFormGroup.value.clientName as string,
            clientSirenSiret: this.accessRightFormGroup.value.clientSirenSiret as string,
            clientRepresentativeName: this.accessRightFormGroup.value
                .clientRepresentativeName as string,
            clientRepresentativeEmail: this.accessRightFormGroup.value
                .clientRepresentativeEmail as string,
            pdlNumber: this.accessRightFormGroup.value.pdlNumber as string,
            validUntil: this.accessRightFormGroup.value.validUntil as Date,
            consumptionAccessStart: this.accessRightFormGroup.value.consumptionAccessStart as Date,
            clientRepresentativePhone: this.accessRightFormGroup.value
                .clientRepresentativePhone as string,
            comments: this.accessRightFormGroup.value.comments as string,
            proofType: this.accessRightFormGroup.value.consentMethod as EnedisAccessRightProofType,
        };
        //create
        this.store
            .dispatch(
                new CreateAccessRight({
                    accessRight: accessRightToCreate,
                    fileProof: this.accessRightFormGroup.value.fileProof,
                }),
            )
            .subscribe({
                next: () => {
                    this.alertService.valid("Nouveau droit d'accès", "Droit d'accès créé !");
                    this.close();
                },
                error: (err) => {
                    console.info(err.error.message);
                    this.alertService.error('Création', err?.error ?? 'Une erreur est survenue');
                },
            });
    }

    isMail(input: string): boolean {
        const expression: RegExp = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
        return expression.test(input);
    }

    public set params(params: { header: any }) {
        this.header = params.header;

        const now = new Date();
        const twoYearsAgo = new Date(now.getFullYear() - 2, now.getMonth(), now.getDate());
        const fiveYearFromNow = new Date(now.getFullYear() + 5, now.getMonth(), now.getDate());

        this.accessRightFormGroup.patchValue({
            clientName: '',
            clientSirenSiret: '',
            clientRepresentativeName: '',
            clientRepresentativeEmail: '',
            pdlNumber: '',
            validUntil: fiveYearFromNow,
            consumptionAccessStart: twoYearsAgo,
            clientRepresentativePhone: '',
            comments: '',
            consentMethod: '',
        });
    }

    public close(): void {
        this.layerService.close();
    }

    public canCreateEdit(): boolean {
        return this.accessRightFormGroup.invalid || !this.accessRightFormGroup.dirty;
    }

    public showPopupOnLayerClose(): boolean {
        return this.accessRightFormGroup.dirty;
    }
}
